import React, {useState, useEffect, useCallback} from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {userProfileService} from "../service/userProfile/userProfile.service";
import {usePasswordValidation} from "../hooks/usePasswordValidation";
import {Profile} from "../components/profile/Profile";
import {ProfileModal} from "../components/modals/ProfileModal";
import {isValidDate} from "../utils/dateValidator";
import {useAuth0} from "@auth0/auth0-react";
import {ACCOUNT_SETTINGS_API_AUDIENCE, SENSITIVE_SCOPE} from "../config/securityContext";
import {ACR} from "../service/auth/ACR";
import {getStepUpMaxAge} from "../config/stepUpPolicy";

const webURL = window.location.origin;

export const ProfilePage = (props) => {
    const auth = useAuth0();
    const [isDeleting, setIsDeleting] = useState(false);
    const {alert, showAlert, setAlert} = props;

    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const [passwordCheck, setPasswordCheck] = useState(false);
    const passwordHandleClose = () => setPasswordCheck(false);
    const passwordHandleShow = () => setPasswordCheck(true);

    const [userProfile, setUserProfile] = useState(null);
    const [initialUserProfile, setInitialUserProfile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [emailDetails, setEmailDetails] = useState({
        email: props?.email
    });
    const [password, setPassword] = useState({
        newPassword: "",
        secondPassword: "",
        oldPassword: ""
    });

    const [passwordDetails, setPasswordDetails] = useState({
        email: ""
    });

    const [error, setError] = useState({
        firstName: "",
        lastName: "",
        phoneNo: "",
        birthday: ""
    });

    const [showProfilePopUp, setShowProfilePopUp] = useState(false);
    const handleCloseProfilePopUp = () => setShowProfilePopUp(false);

    const [validLength, hasNumber, upperCase, lowerCase, specialChar, passwordMatch] = usePasswordValidation({
        firstPassword: password.newPassword,
        secondPassword: password.secondPassword
    });


    useEffect(() => {
        showAlert(props?.alert);
    }, [props?.alert]);

    useEffect(() => {
        if (auth.isAuthenticated && props?.isCustomerInfoFetched) {
            const isInvalid =
                !props?.firstName?.trim() ||
                !props?.lastName?.trim() ||
                !props?.phoneNo?.trim();

            setShowProfilePopUp(isInvalid);

            const initialProfile = {
                firstName: props.firstName,
                lastName: props.lastName,
                email: props.email,
                phoneNo: props.phoneNo,
                birthday: props.birthday,
                name: `${props.firstName} ${props.lastName}`.trim(),
            };
            setInitialUserProfile(initialProfile);
            setUserProfile(initialProfile);
        }
    }, [props?.isCustomerInfoFetched, auth.isAuthenticated]);

    const handleDelete = useCallback(async () => {
        const userConfirmed = window.confirm(
            "Do you want to delete your account?"
        );

        if (!userConfirmed) return;

        setIsDeleting(true);

        try {
            await auth.loginWithRedirect({
                authorizationParams: {
                    connection: "customers-db",
                    audience: ACCOUNT_SETTINGS_API_AUDIENCE,
                    scope: `openid profile email ${SENSITIVE_SCOPE.DELETE_ACCOUNT}`,
                    acr_values: ACR.MFA,
                    login_hint: props?.email || "",
                    max_age: getStepUpMaxAge(SENSITIVE_SCOPE.DELETE_ACCOUNT),
                },
                appState: {
                    returnTo: "/account-settings",
                    pendingOperation: {
                        type: "api_scope",
                        scope: SENSITIVE_SCOPE.DELETE_ACCOUNT,
                    },
                },
            });
        } finally {
            setIsDeleting(false);
        }
    }, [auth, props?.email]);

    const emailHandleChange = (event) => {
        setEmailDetails({
            ...emailDetails,
            [event.target.name]: event.target.value
        });
    };


    const validate = () => {
        let pattern;
        let input = userProfile;
        let errors = {};
        let isValid = true;

        if (!input["firstName"]) {
            isValid = false;
            errors["firstName"] = "Please enter valid first name.";
        }

        if (!input["lastName"]) {
            isValid = false;
            errors["lastName"] = "Please enter valid last name.";
        }

        if (input["firstName"] || input["lastName"]) {
            let namePattern = new RegExp(/^[a-zA-Z ]*$/);

            if (!namePattern.test(input["firstName"])) {
                isValid = false;
                errors["firstName"] = "Please enter valid first name.";
            }

            if (!namePattern.test(input["lastName"])) {
                isValid = false;
                errors["lastName"] = "Please enter valid last name.";
            }
        }

        if (input["birthday"]) {
            errors["birthday"] = isValidDate(input["birthday"]);
            if (errors["birthday"]) isValid = false;
        }

        if (!input["phoneNo"]) {
            isValid = false;
            errors["phoneNo"] = "Please enter your phone number.";
        }

        if (typeof input["phoneNo"] !== "undefined") {
            pattern = new RegExp(/^[0-9\b]+$/);

            if (!pattern.test(input["phoneNo"])) {
                isValid = false;
                errors["phoneNo"] = "Please enter only numbers.";
            } else if (input["phoneNo"].length !== 10) {
                isValid = false;
                errors["phoneNo"] = "Please enter a valid phone number.";
            }
        }

        setError(errors);
        return isValid;
    };

    const handleChange = (event) => {
        const {name, value} = event.target;

        setUserProfile((prev) => {
            const updated = {
                ...prev,
                [name]: value,
            };
            updated.name = `${updated.firstName} ${updated.lastName}`.trim();
            return updated;
        });
    };


    const handleSubmit = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            const data = await userProfileService.updateProfile(userProfile, props?.email);
            setInitialUserProfile(userProfile);
            if (data && data.statusCode !=="200") {
                setAlert({"msg": "Sorry, update your profile failed.", "type": "danger"});
                return;
            }

            if (showProfilePopUp) {
                handleCloseProfilePopUp();
            }
            setAlert({"msg": "Profile updated successfully.", "type": "success"});

        } catch (error) {
            console.error("Profile update failed:", error);
            setAlert({"msg": "Sorry, we are unable to update your profile at this time.", "type": "danger"});

        } finally {
            setIsSaving(false);
        }
    };

    const passwordSubmit = (event) => {
        event.preventDefault();

        if (validLength && hasNumber && upperCase && lowerCase && specialChar && passwordMatch) {
            return userProfileService.updatePassword(passwordDetails).then((data) => {
                if (data.message === "Password is changed successfully") {
                    showAlert("your password is successfully changed. Signing you out", "success");
                    localStorage.clear();
                    window.location.href = `${webURL}`;
                } else {
                    showAlert("error", "danger");
                }
            });
        }
    };

    const passwordHandleChange = (event) => {
        setPassword({
            ...password,
            [event.target.name]: event.target.value
        });

        setPasswordDetails({
            oldPassword: password.oldPassword,
            newPassword: password.newPassword,
            email: props.email
        });
    };

    const prof = {
        handleSubmit,
        handleDelete,
        handleChange,
        emailHandleChange,
        passwordHandleChange,
        passwordSubmit,
        validLength,
        upperCase,
        lowerCase,
        specialChar,
        hasNumber,
        passwordMatch,
        password,
        error,
        alert,
        showAlert,
        userProfile,
        initialUserProfile,
        show,
        handleClose,
        handleShow,
        passwordCheck,
        passwordHandleClose,
        passwordHandleShow,
        isDeleting,
        isSaving
    };

    const profileModalDetails = {
        handleSubmit,
        handleChange,
        handleCloseProfilePopUp,
        userProfile,
        error,
        showProfilePopUp,
        alert
    };

    return (
        <div>
            {showProfilePopUp ? <ProfileModal {...profileModalDetails} /> : null}
            <Profile {...prof} />
        </div>
    );
};